// ── Lo que viene adjunto a un mensaje ────────────────────────────────────────
// Una foto, un video o un documento dentro de la burbuja de la conversación.
// El audio no pasa por acá: tiene su propio reproductor (VoiceNote).
//
// Todo se muestra EN la conversación. Una factura que el cliente mandó por
// WhatsApp y hay que bajar a la computadora para verla es una factura que nadie
// mira hasta que hace falta.
//
// API cerrada (REGLA #16): ni className ni style. Entra el archivo y su estado.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon.js';
import { Modal } from './Modal.js';
import { Pressable } from './Pressable.js';

export interface ChatAdjuntoProps {
  kind: string;
  /** URL del archivo. null mientras no se bajó del proveedor. */
  url: string | null;
  mimeType?: string | null;
  filename?: string | null;
  sizeBytes?: number | null;
  error?: string | null;
}

function pesoLegible(bytes: number | null | undefined): string | null {
  if (bytes == null || bytes <= 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ChatAdjunto({ kind, url, mimeType, filename, sizeBytes, error = null }: ChatAdjuntoProps) {
  const { t } = useTranslation();
  const [ampliada, setAmpliada] = useState(false);

  if (!url) {
    return (
      <div style={pendiente}>
        <span style={{ color: 'var(--evo-fg-3)', display: 'inline-flex' }}>
          <Icon name={error ? 'alert' : 'clock'} size={14} />
        </span>
        <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)' }}>
          {error ? t('messages.attachmentFailed') : t('messages.attachmentDownloading')}
        </span>
      </div>
    );
  }

  if (kind === 'image') {
    return (
      <>
        {/* Se abre en grande sin salir de la conversación: una captura de
            pantalla a tamaño de miniatura no se lee, y mandar a otra pestaña
            para leerla es perder el hilo. */}
        <Pressable onPress={() => setAmpliada(true)} ariaLabel={t('messages.attachmentOpen')}>
          <img
            src={url}
            alt={filename ?? t('messages.attachmentImage')}
            style={{
              display: 'block', maxWidth: '100%', maxHeight: 'var(--evo-chat-media-max-h)',
              borderRadius: 'var(--evo-r)', objectFit: 'cover',
            }}
          />
        </Pressable>
        <Modal open={ampliada} onClose={() => setAmpliada(false)} title={filename ?? t('messages.attachmentImage')} size="lg">
          <img src={url} alt={filename ?? ''} style={{ display: 'block', width: '100%', height: 'auto', borderRadius: 'var(--evo-r)' }} />
        </Modal>
      </>
    );
  }

  if (kind === 'video') {
    return (
      <video
        src={url}
        controls
        preload="metadata"
        style={{
          display: 'block', maxWidth: '100%', maxHeight: 'var(--evo-chat-media-max-h)',
          borderRadius: 'var(--evo-r)', background: 'var(--evo-bg-3)',
        }}
      />
    );
  }

  // Documento y cualquier otra cosa con archivo: fila para abrirlo.
  const peso = pesoLegible(sizeBytes);
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      style={{
        display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)',
        padding: 'var(--evo-space-2) var(--evo-space-3)',
        background: 'var(--evo-bg-2)', borderRadius: 'var(--evo-r)',
        textDecoration: 'none', color: 'inherit', maxWidth: '100%',
      }}
    >
      <span style={{ color: 'var(--evo-fg-3)', display: 'inline-flex', flexShrink: 0 }}>
        <Icon name="attach" size={14} />
      </span>
      <span style={{
        flex: 1, minWidth: 0, fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-1)',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {filename ?? mimeType ?? t('messages.attachmentFile')}
      </span>
      {peso && (
        <span style={{ flexShrink: 0, fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-4)' }}>{peso}</span>
      )}
      <span style={{ color: 'var(--evo-fg-3)', display: 'inline-flex', flexShrink: 0 }}>
        <Icon name="download" size={14} />
      </span>
    </a>
  );
}

const pendiente = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--evo-space-3)',
  padding: 'var(--evo-space-2) var(--evo-space-3)',
  background: 'var(--evo-bg-2)',
  borderRadius: 'var(--evo-r)',
} as const;
